import { motion } from "framer-motion";
import { Moon, Sun } from "lucide-react";
import { ProjectLogo } from "./Logo";
import { useLang } from "../i18n";

const ThemePicker = ({ onChoose }) => {
  const { t } = useLang();
  return (
  <motion.div
    data-testid="theme-picker"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.4 }}
    className="fixed inset-0 z-[120] flex items-center justify-center bg-[#05070e]/92 px-6 backdrop-blur-xl"
  >
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-2xl text-center"
    >
      <div className="flex justify-center">
        <ProjectLogo className="h-12 w-12" />
      </div>
      <p className="mt-6 font-mono text-sm uppercase tracking-[0.3em] text-white/50">
        The Lifespan Project
      </p>
      <h2 className="mt-4 font-display text-3xl font-semibold tracking-tight text-white sm:text-4xl">
        {t("pick.title", "Choose your view")}
      </h2>
      <p className="mt-3 text-base font-light text-white/60">
        {t("pick.sub", "Pick a theme to begin. You can switch anytime from the header.")}
      </p>
      <div className="mt-10 grid gap-5 sm:grid-cols-2">
        <button
          type="button"
          data-testid="theme-picker-dark"
          onClick={() => onChoose("dark")}
          className="group rounded-3xl border border-white/15 bg-[#070B16] p-6 text-left transition-shadow duration-300 hover:border-cyan-300/50 hover:shadow-[0_16px_60px_rgba(34,211,238,0.15)]"
        >
          <div className="rounded-2xl border border-white/10 bg-[#0C1322] p-5">
            <div className="h-2.5 w-24 rounded-full bg-gradient-to-r from-violet-400 via-blue-400 to-cyan-300" />
            <div className="mt-3 h-2 w-36 rounded-full bg-white/25" />
            <div className="mt-2 h-2 w-28 rounded-full bg-white/15" />
            <div className="mt-4 flex gap-2">
              <div className="h-6 w-16 rounded-full bg-gradient-to-r from-emerald-400 to-lime-400" />
              <div className="h-6 w-16 rounded-full bg-gradient-to-r from-violet-500 via-blue-500 to-cyan-400" />
            </div>
          </div>
          <div className="mt-5 flex items-center gap-3">
            <Moon className="h-5 w-5 text-cyan-300" />
            <span className="text-lg font-semibold text-white">{t("pick.dark", "Dark")}</span>
            <span className="text-sm text-white/40">{t("pick.dark.sub", "Cinematic")}</span>
          </div>
        </button>
        <button
          type="button"
          data-testid="theme-picker-light"
          onClick={() => onChoose("light")}
          className="group rounded-3xl border border-white/15 bg-white p-6 text-left transition-shadow duration-300 hover:border-blue-400/60 hover:shadow-[0_16px_60px_rgba(59,130,246,0.25)]"
        >
          <div className="rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-100 p-5">
            <div className="h-2.5 w-24 rounded-full bg-gradient-to-r from-violet-600 via-blue-600 to-cyan-600" />
            <div className="mt-3 h-2 w-36 rounded-full bg-slate-300" />
            <div className="mt-2 h-2 w-28 rounded-full bg-slate-200" />
            <div className="mt-4 flex gap-2">
              <div className="h-6 w-16 rounded-full bg-gradient-to-r from-emerald-400 to-lime-400" />
              <div className="h-6 w-16 rounded-full bg-gradient-to-r from-violet-500 via-blue-500 to-cyan-400" />
            </div>
          </div>
          <div className="mt-5 flex items-center gap-3">
            <Sun className="h-5 w-5 text-amber-500" />
            <span className="text-lg font-semibold text-slate-900">{t("pick.light", "Light")}</span>
            <span className="text-sm text-slate-400">{t("pick.light.sub", "Gradient white")}</span>
          </div>
        </button>
      </div>
    </motion.div>
  </motion.div>
  );
};

export default ThemePicker;
